/**
 * Turns an HTML fragment from the CMS into plain text.
 *
 * Used both for rendering (the FAQ accordion) and for building JSON-LD, so the
 * structured data and the visible text are derived from the same source and
 * cannot disagree.
 *
 * Prefers DOMParser, which decodes entities correctly (`&nbsp;`, `&amp;`), and
 * falls back to tag-stripping if DOMParser is unavailable.
 */
export function stripHtml(html) {
  if (!html) return "";

  if (typeof window !== "undefined" && typeof window.DOMParser !== "undefined") {
    const doc = new window.DOMParser().parseFromString(html, "text/html");
    return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  }

  return String(html)
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Demotes any <h1> inside a CMS-supplied HTML fragment to <h2>.
 *
 * The pages that render admin-managed HTML (/terms, /privacy-policy) already
 * have their own hero <h1>. The privacy policy body also contains an <h1>,
 * which gave that page two competing top-level headings. The page's hero is
 * the authoritative heading, so body headings shift down a level.
 */
export function demoteHeadings(html) {
  if (!html) return "";
  return String(html)
    .replace(/<h1(\s[^>]*)?>/gi, (_, attrs) => `<h2${attrs || ""}>`)
    .replace(/<\/h1>/gi, "</h2>");
}

/** Clamps text to a meta-description-friendly length on a word boundary. */
export function truncate(text, max = 160) {
  const clean = stripHtml(text);
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

export default { stripHtml, truncate, demoteHeadings };
